<!-- ============================================================
     ONBOARDING MODE — hero-style dark fullscreen layout
     ============================================================ -->
<ng-container *ngIf="isOnboardingMode">
  <div class="ob-root">
    <!-- Video background (same as website hero) -->
    <video class="ob-video" autoplay muted loop playsinline>
      <source src="https://www.meetzy.io/assets/build-agent-bg.mp4" type="video/mp4" />
    </video>
    <div class="ob-overlay"></div>

    <!-- Centered glass card -->
    <div class="ob-wrap">
      <!-- Logo -->
      <a href="https://www.meetzy.io" class="ob-logo">
        <img src="assets/images/meetzy_white.png" alt="Meetzy" class="h-7 w-auto" />
      </a>

      <!-- Card -->
      <div class="ob-card">
        <!-- Eyebrow -->
        <p class="ob-eyebrow">Get started</p>

        <!-- Headline -->
        <h1 class="ob-heading">Your agent is<br />ready to build.</h1>

        <p class="ob-sub">Sign in to create your agent in minutes.</p>

        <!-- Error -->
        <p *ngIf="errorMessage" class="ob-error">{{ errorMessage }}</p>

        <!-- OAuth buttons -->
        <div class="ob-btns">
          <button type="button" (click)="onSignInWithGoogle()" class="ob-oauth-btn">
            <svg class="ob-oauth-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z" fill="#EA4335" />
              <path d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z" fill="#4285F4" />
              <path d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z" fill="#FBBC05" />
              <path d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z" fill="#34A853" />
            </svg>
            Continue with Google
          </button>

          <button type="button" (click)="onSignInWithMicrosoft()" class="ob-oauth-btn">
            <svg class="ob-oauth-icon" viewBox="0 0 23 23" aria-hidden="true">
              <path d="M0 0h11v11H0z" fill="#F25022"/>
              <path d="M12 0h11v11H12z" fill="#7FBA00"/>
              <path d="M0 12h11v11H0z" fill="#00A4EF"/>
              <path d="M12 12h11v11H12z" fill="#FFB900"/>
            </svg>
            Continue with Microsoft
          </button>
        </div>

        <!-- Back link -->
        <a href="https://www.meetzy.io" class="ob-back">
          ← Back to meetzy.io
        </a>
      </div>

      <!-- Trust line -->
      <p class="ob-trust">
        <svg class="inline w-3 h-3 mb-0.5 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Secure sign-in
      </p>
    </div>
  </div>
</ng-container>


<!-- ============================================================
     STANDARD MODE — hero-style glass card layout
     ============================================================ -->
<ng-container *ngIf="!isOnboardingMode">
  <div class="std-root" [class.std-light]="!isDarkModeActive">
    <!-- Video background -->
    <video class="std-video" autoplay muted loop playsinline>
      <source src="https://www.meetzy.io/assets/build-agent-bg.mp4" type="video/mp4" />
    </video>
    <div class="std-overlay"></div>

    <!-- Theme toggle pill -->
    <button class="std-theme-toggle" (click)="toggleDarkMode()" aria-label="Toggle dark/light mode">
      <!-- Sun icon — shown in dark mode -->
      <svg *ngIf="isDarkModeActive" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
      </svg>
      <!-- Moon icon — shown in light mode -->
      <svg *ngIf="!isDarkModeActive" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
      </svg>
      <span>{{ isDarkModeActive ? 'Light mode' : 'Dark mode' }}</span>
    </button>

    <!-- Card -->
    <div class="std-wrap">
      <!-- Logo -->
      <a href="https://www.meetzy.io" class="std-logo-link">
        <img class="std-logo-img"
             [src]="domain.includes('innovates.beyondcx.ai') ? 'assets/images/beyond_logo.jpeg' : domain.includes('dashboard.dex360.net') ? 'assets/images/dex360.png' : (isDarkModeActive ? 'assets/images/meetzy_white.png' : 'assets/images/meetzy_logo_transparent.png')"
             alt="Logo" />
      </a>

      <div class="std-card">
        <p class="std-eyebrow">Welcome back</p>
        <h1 class="std-heading">Sign in to<br />your account.</h1>

        <!-- Error shown before form is expanded -->
        <p *ngIf="errorMessage && !showEmailForm" class="std-msg std-msg-error">{{ errorMessage }}</p>

        <!-- OAuth buttons — primary actions (full-width) -->
        <div class="std-btns">
          <button type="button" (click)="onSignInWithGoogle()" class="std-oauth-btn-full">
            <svg class="std-oauth-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z" fill="#EA4335" />
              <path d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z" fill="#4285F4" />
              <path d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z" fill="#FBBC05" />
              <path d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z" fill="#34A853" />
            </svg>
            Continue with Google
          </button>

          <button type="button" (click)="onSignInWithMicrosoft()" class="std-oauth-btn-full">
            <svg class="std-oauth-icon" viewBox="0 0 23 23" aria-hidden="true">
              <path d="M0 0h11v11H0z" fill="#F25022"/>
              <path d="M12 0h11v11H12z" fill="#7FBA00"/>
              <path d="M0 12h11v11H0z" fill="#00A4EF"/>
              <path d="M12 12h11v11H12z" fill="#FFB900"/>
            </svg>
            Continue with Microsoft
          </button>
        </div>

        <!-- Divider -->
        <div class="std-divider">
          <span class="std-divider-line"></span>
          <span class="std-divider-text">or</span>
          <span class="std-divider-line"></span>
        </div>

        <!-- Email toggle button -->
        <button type="button" class="std-email-btn" (click)="showEmailForm = !showEmailForm">
          <svg class="std-oauth-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
          Sign in with email
        </button>

        <!-- Collapsible email / password form -->
        <div *ngIf="showEmailForm" class="std-email-form">
          <p *ngIf="errorMessage" class="std-msg std-msg-error">{{ errorMessage }}</p>
          <p *ngIf="successMessage" class="std-msg std-msg-success">{{ successMessage }}</p>

          <form [formGroup]="loginForm" (ngSubmit)="onSubmit()">
            <div class="std-field">
              <label for="email" class="std-label">Email</label>
              <input id="email" name="email" formControlName="email"
                     type="email" autocomplete="email"
                     placeholder="you@company.com" required
                     class="std-input" />
            </div>

            <div class="std-field std-field-mt">
              <div class="std-label-row">
                <label for="password" class="std-label">Password</label>
                <button type="button" [disabled]="loading" (click)="onForgotPassword()"
                        class="std-forgot">
                  {{ loading ? 'Sending…' : 'Forgot password?' }}
                </button>
              </div>
              <div class="std-input-wrap">
                <input [type]="showPassword ? 'text' : 'password'"
                       id="password" name="password" formControlName="password"
                       autocomplete="current-password"
                       placeholder="••••••••••" required
                       class="std-input std-input-pw" />
                <button type="button" (click)="togglePasswordVisibility()" class="std-eye">
                  <svg *ngIf="!showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <svg *ngIf="showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                </button>
              </div>
            </div>

            <button type="submit" class="std-submit std-submit-mt">{{ loginBtnText }}</button>
          </form>
        </div>

        <!-- Sign up link -->
        <p *ngIf="domain.includes('dashboard.meetzy.io')" class="std-signup-link">
          Don't have an account?
          <a href="https://www.meetzy.io/signup">Get started free →</a>
        </p>
      </div>

      <p class="std-trust">
        <svg style="display:inline;width:12px;height:12px;vertical-align:-2px;opacity:.6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
        </svg>
        Secure sign-in
      </p>
    </div>
  </div>
</ng-container>

<!-- 2FA Verification Modal -->
<app-two-factor-modal
  [show]="show2FAModal"
  (verified)="on2FAVerified()"
  (cancelled)="on2FACancelled()">
</app-two-factor-modal>
